import { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { personalInfo } from '../data/portfolio';

const ROLES = ['Full Stack Developer', 'AI / ML Engineer', 'Backend Architect', 'MERN Specialist', 'RAG / LangChain Dev'];

function useTypewriter(words, speed = 75, pause = 2200) {
  const [display, setDisplay] = useState('');
  const [wi, setWi] = useState(0);
  const [ci, setCi] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    const cur = words[wi];
    const t = setTimeout(() => {
      if (!del) {
        setDisplay(cur.slice(0, ci + 1));
        if (ci + 1 === cur.length) setTimeout(() => setDel(true), pause);
        else setCi(c => c + 1);
      } else {
        setDisplay(cur.slice(0, ci - 1));
        if (ci - 1 === 0) { setDel(false); setWi(i => (i + 1) % words.length); setCi(0); }
        else setCi(c => c - 1);
      }
    }, del ? 35 : speed);
    return () => clearTimeout(t);
  }, [ci, del, wi, words, speed, pause]);
  return display;
}

// ── LOADING SCREEN ────────────────────────────────────────────────────────────
function LoadingScreen({ onDone }) {
  const LINE1 = 'DHARSHITH'.split('');
  const LINE2 = 'DEV KP'.split('');
  const PER = 0.08;
  const TOTAL = (LINE1.length + LINE2.length) * PER + 0.6;

  useEffect(() => {
    const t = setTimeout(onDone, TOTAL * 1000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.04, filter: 'blur(8px)' }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'var(--bg)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: '0.25rem',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(0,245,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,245,255,0.03) 1px,transparent 1px)', backgroundSize: '60px 60px' }} />
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 2, repeat: Infinity }}
        style={{ position: 'absolute', width: '40vw', height: '40vw', maxWidth: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,245,255,0.07) 0%, transparent 70%)', filter: 'blur(20px)', pointerEvents: 'none' }}
      />

      <div style={{ display: 'flex', overflow: 'hidden' }}>
        {LINE1.map((ch, i) => (
          <motion.span key={i} initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * PER, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 9vw, 5.5rem)', letterSpacing: '0.06em', color: 'var(--white)', lineHeight: 1, display: 'inline-block' }}>
            {ch}
          </motion.span>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.15em', overflow: 'hidden' }}>
        {LINE2.map((ch, i) => (
          <motion.span key={i} initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (LINE1.length + i) * PER, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 9vw, 5.5rem)', letterSpacing: '0.06em', color: ch === ' ' ? 'transparent' : 'var(--cyan)', textShadow: ch !== ' ' ? '0 0 40px rgba(0,245,255,0.5)' : 'none', lineHeight: 1, display: 'inline-block', minWidth: ch === ' ' ? '0.35em' : 'auto' }}>
            {ch === ' ' ? '\u00A0' : ch}
          </motion.span>
        ))}
      </div>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: TOTAL - 0.5, duration: 0.4 }}
        style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(0.5rem, 1.5vw, 0.72rem)', letterSpacing: '0.3em', color: 'var(--grey)', textTransform: 'uppercase', marginTop: '0.75rem', position: 'relative', zIndex: 1 }}>
        Full Stack Developer · AI Engineer
      </motion.p>

      <div style={{ position: 'absolute', bottom: '12%', width: 'clamp(120px, 25vw, 200px)', height: 1, background: 'rgba(0,245,255,0.12)', overflow: 'hidden' }}>
        <motion.div initial={{ x: '-100%' }} animate={{ x: '0%' }} transition={{ duration: TOTAL - 0.15, ease: 'linear' }}
          style={{ width: '100%', height: '100%', background: 'linear-gradient(to right, var(--cyan), #7c3aed)', boxShadow: '0 0 8px rgba(0,245,255,0.8)' }} />
      </div>
    </motion.div>
  );
}

function NeonGrid() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <div className="grid-lines" style={{ opacity: 0.6 }} />
      <motion.div
        animate={{ y: ['-100%', '200%'] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'linear', repeatDelay: 4 }}
        style={{ position: 'absolute', left: 0, right: 0, height: '2px', background: 'linear-gradient(to right, transparent, rgba(0,245,255,0.3), transparent)', boxShadow: '0 0 20px rgba(0,245,255,0.5)' }}
      />
      {['top:0;left:0', 'top:0;right:0', 'bottom:0;left:0', 'bottom:0;right:0'].map((pos, i) => {
        const styles = Object.fromEntries(pos.split(';').map(s => s.split(':')));
        const rotate = [0, 90, 270, 180][i];
        return (
          <div key={i} style={{ position: 'absolute', ...styles, width: 40, height: 40, margin: 16 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 + i * 0.2, duration: 0.5 }}
              style={{ position: 'absolute', inset: 0, borderTop: '1.5px solid var(--cyan)', borderLeft: '1.5px solid var(--cyan)', borderRadius: '2px 0 0 0', boxShadow: '-2px -2px 8px rgba(0,245,255,0.3)', transform: `rotate(${rotate}deg)`, transformOrigin: 'center' }} />
          </div>
        );
      })}
      {[...Array(18)].map((_, i) => (
        <motion.div key={i}
          style={{ position: 'absolute', width: 2, height: 2, borderRadius: '50%', background: 'var(--cyan)', left: `${5 + (i * 17) % 90}%`, top: `${10 + (i * 13) % 80}%`, boxShadow: '0 0 6px var(--cyan)' }}
          animate={{ y: [0, -30, 0], opacity: [0, 1, 0], scale: [0, 1.5, 0] }}
          transition={{ duration: 3 + (i % 4), repeat: Infinity, delay: i * 0.35, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

function GlowOrb() {
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
      style={{ position: 'absolute', top: '50%', left: '60%', transform: 'translate(-50%, -50%)', width: '60vw', height: '60vw', maxWidth: 700, maxHeight: 700, borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(0,245,255,0.06) 0%, rgba(0,180,216,0.04) 40%, transparent 70%)', filter: 'blur(1px)', pointerEvents: 'none' }}
    />
  );
}

// ── CLEAN PROFESSIONAL PHOTO ──────────────────────────────────────────────────
function PhotoCard() {
  const ref = useRef(null);
  const [hovered, setHovered] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 100, damping: 22 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 22 });
  const rotateX = useTransform(springY, [-0.5, 0.5], ['8deg', '-8deg']);
  const rotateY = useTransform(springX, [-0.5, 0.5], ['-8deg', '8deg']);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { mouseX.set(0); mouseY.set(0); setHovered(false); }}
      style={{ position: 'relative', width: 300, perspective: '1000px' }}
    >
      {/* Subtle ambient glow behind — not on the photo itself */}
      <motion.div
        animate={{ opacity: hovered ? 0.6 : 0.3 }}
        transition={{ duration: 0.5 }}
        style={{
          position: 'absolute',
          inset: -30,
          borderRadius: '24px',
          background: 'radial-gradient(ellipse at 50% 80%, rgba(0,245,255,0.12) 0%, transparent 70%)',
          filter: 'blur(20px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* 3D tilt wrapper */}
      <motion.div
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Photo container — clean rectangle with subtle border */}
        <motion.div
          animate={{
            y: hovered ? -6 : 0,
            boxShadow: hovered
              ? '0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,245,255,0.15), 0 0 40px rgba(0,245,255,0.06)'
              : '0 16px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
          }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          style={{
            borderRadius: '20px',
            overflow: 'hidden',
            position: 'relative',
            background: 'transparent',
          }}
        >
          {/* Thin top accent line — only decoration kept */}
          <motion.div
            animate={{ opacity: hovered ? 1 : 0.4, scaleX: hovered ? 1 : 0.5 }}
            transition={{ duration: 0.4 }}
            style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              height: 2, zIndex: 2,
              background: 'linear-gradient(to right, transparent, rgba(0,245,255,0.7), transparent)',
              transformOrigin: 'center',
            }}
          />

          {/* The photo itself */}
          <img
            src={personalInfo.photo}
            alt={personalInfo.name}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'center top',
              filter: hovered
                ? 'brightness(1.05) contrast(1.02) saturate(1.08)'
                : 'brightness(0.97) contrast(1) saturate(1)',
              transition: 'filter 0.5s ease',
            }}
            onError={e => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement.style.background = 'linear-gradient(160deg, rgba(0,245,255,0.08), rgba(0,0,0,0.4))';
              e.currentTarget.parentElement.style.height = '380px';
              e.currentTarget.parentElement.innerHTML += '<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:var(--font-display);font-size:5rem;color:rgba(0,245,255,0.4)">DD</div>';
            }}
          />

          {/* Very subtle dark gradient at bottom for text legibility */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: '35%',
            background: 'linear-gradient(to top, rgba(4,7,15,0.6) 0%, transparent 100%)',
            pointerEvents: 'none',
            zIndex: 1,
          }} />

          {/* Name + role overlaid at bottom of photo */}
          <motion.div
            animate={{ opacity: hovered ? 1 : 0, y: hovered ? 0 : 8 }}
            transition={{ duration: 0.35 }}
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '1.25rem 1.25rem 1rem',
              zIndex: 3,
            }}
          >
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.7rem', letterSpacing: '0.2em', color: 'var(--white)', marginBottom: '0.2rem' }}>
              DHARSHITH DEV KP
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.52rem', letterSpacing: '0.12em', color: 'rgba(0,245,255,0.7)' }}>
              FULLSTACK · AI ENGINEER
            </div>
          </motion.div>
        </motion.div>

        {/* Status badge — outside photo, bottom right */}
        <motion.div
          animate={{ scale: hovered ? 1.05 : 1 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'absolute',
            bottom: -14,
            right: 16,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.35rem 0.9rem',
            background: 'rgba(4,7,15,0.9)',
            border: '1px solid rgba(16,185,129,0.3)',
            borderRadius: '100px',
            backdropFilter: 'blur(12px)',
            zIndex: 4,
          }}
        >
        </motion.div>
      </motion.div>

      {/* Stats grid — below photo */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        style={{
          marginTop: '1.75rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.6rem',
        }}
      >
        {[
          { num: '8+', label: 'Projects' },
          { num: '3', label: 'Certs' },
          { num: '1.6+', label: 'Years Exp' },
          { num: 'MERN', label: 'Core Stack' },
        ].map((s) => (
          <motion.div
            key={s.label}
            whileHover={{ scale: 1.05, borderColor: 'rgba(0,245,255,0.2)' }}
            style={{
              textAlign: 'center',
              padding: '0.65rem 0.5rem',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              transition: 'border-color 0.2s',
              cursor: 'default',
            }}
          >
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: s.num === 'MERN' ? '0.9rem' : '1.25rem',
              fontWeight: 900,
              color: 'var(--white)',
              lineHeight: 1,
            }}>
              {s.num}
            </div>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: '0.46rem',
              letterSpacing: '0.1em',
              color: 'var(--grey)',
              marginTop: '0.3rem',
              textTransform: 'uppercase',
            }}>
              {s.label}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } } };
const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } } };
const fadeLeft = { hidden: { opacity: 0, x: -60 }, visible: { opacity: 1, x: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } } };

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const role = useTypewriter(ROLES);

  return (
    <>
      <AnimatePresence>
        {!loaded && <LoadingScreen onDone={() => setLoaded(true)} />}
      </AnimatePresence>

      <section id="home" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', paddingTop: 80 }}>
        <NeonGrid />
        <GlowOrb />

        <div className="container" style={{ position: 'relative', zIndex: 2, width: '100%', paddingTop: '3rem', paddingBottom: '5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'clamp(2rem, 5vw, 4rem)', alignItems: 'center' }} className="hero-grid">

            {/* LEFT */}
            <motion.div variants={stagger} initial="hidden" animate={loaded ? 'visible' : 'hidden'}>
              <motion.div variants={fadeUp} style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 1rem', background: 'rgba(0,245,255,0.05)', border: '1px solid rgba(0,245,255,0.2)', borderRadius: '100px', backdropFilter: 'blur(10px)' }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.6rem', letterSpacing: '0.15em', color: 'var(--cyan)' }}>
                    OPEN TO OPPORTUNITIES
                  </span>
                </div>
              </motion.div>

              <motion.div variants={fadeLeft}>
                <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(3rem, 8vw, 6.5rem)', fontWeight: 700, lineHeight: 0.95, letterSpacing: '-0.02em', marginBottom: '0.25rem' }}>
                  <span style={{ display: 'block', color: 'var(--white)' }}>DHARSHITH</span>
                  <span style={{ display: 'block', color: 'var(--white)' }}>DEV</span>
                  <span style={{ display: 'block', background: 'linear-gradient(90deg, var(--cyan), var(--teal), var(--cyan))', backgroundSize: '200%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', filter: 'drop-shadow(0 0 20px rgba(0,245,255,0.4))', animation: 'shimmer 3s ease-in-out infinite' }}>KP.</span>
                </h1>
              </motion.div>

              <motion.div variants={fadeUp} style={{ margin: '1.5rem 0 1rem' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(0.75rem, 2vw, 1rem)', letterSpacing: '0.1em', color: 'var(--grey)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: 'var(--cyan)', opacity: 0.7 }}>▶</span>
                  <span style={{ color: 'var(--cyan)', textShadow: '0 0 15px rgba(0,245,255,0.5)' }}>{role}</span>
                  <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.5, repeat: Infinity }}
                    style={{ width: 2, height: '1em', background: 'var(--cyan)', display: 'inline-block', boxShadow: '0 0 8px var(--cyan)' }} />
                </div>
              </motion.div>

              <motion.p variants={fadeUp} style={{ fontSize: 'clamp(0.85rem, 1.8vw, 1rem)', color: 'var(--grey)', maxWidth: 540, lineHeight: 1.75, marginBottom: '2.5rem' }}>
                {personalInfo.tagline}
              </motion.p>

              <motion.div variants={fadeUp} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2.5rem' }}>
                <motion.a href="#projects" className="btn-cyan" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  View Projects
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m9 18 6-6-6-6"/></svg>
                </motion.a>
                <motion.a href="#contact" className="btn-outline" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  Let's Talk
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </motion.a>
                <motion.a href={personalInfo.resumeUrl} download className="btn-outline" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Resume
                </motion.a>
              </motion.div>

              <motion.div variants={fadeUp} style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.55rem', letterSpacing: '0.2em', color: 'var(--grey2)', textTransform: 'uppercase' }}>Connect</span>
                <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, var(--glass-border), transparent)', maxWidth: 60 }} />
                {[
                  { href: personalInfo.github, label: 'GitHub', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg> },
                  { href: personalInfo.linkedin, label: 'LinkedIn', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
                  { href: `mailto:${personalInfo.email}`, label: 'Email', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg> },
                ].map(({ href, label, icon }) => (
                  <motion.a key={label} href={href} target="_blank" rel="noopener noreferrer" whileHover={{ y: -4, scale: 1.15 }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '10px', background: 'rgba(0,245,255,0.04)', border: '1px solid rgba(0,245,255,0.12)', color: 'var(--grey)', transition: 'color 0.2s, border-color 0.2s, box-shadow 0.2s, background 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--cyan)'; e.currentTarget.style.borderColor = 'rgba(0,245,255,0.4)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(0,245,255,0.2)'; e.currentTarget.style.background = 'rgba(0,245,255,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--grey)'; e.currentTarget.style.borderColor = 'rgba(0,245,255,0.12)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.background = 'rgba(0,245,255,0.04)'; }}>
                    {icon}
                  </motion.a>
                ))}
              </motion.div>
            </motion.div>

            {/* RIGHT — photo */}
            <motion.div
              initial={{ opacity: 0, x: 60, scale: 0.92 }}
              animate={loaded ? { opacity: 1, x: 0, scale: 1 } : {}}
              transition={{ duration: 1.2, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="hero-photo-col"
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <PhotoCard />
            </motion.div>
          </div>

          {/* Mobile name tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
            className="mobile-name-tag"
            style={{ display: 'none', textAlign: 'center', marginTop: '1rem', marginBottom: '1.5rem' }}
          >
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.75rem', letterSpacing: '0.18em', color: 'var(--cyan)', textShadow: '0 0 10px rgba(0,245,255,0.3)' }}>
              DHARSHITH DEV KP
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.6rem', letterSpacing: '0.1em', color: 'var(--grey2)', marginTop: '0.35rem' }}>
              FULLSTACK · AI ENGINEER
            </div>
          </motion.div>

          {/* Scroll hint */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={loaded ? { opacity: 1 } : {}}
            transition={{ delay: 2.5 }}
            style={{ position: 'absolute', bottom: '-3rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}
          >
            <motion.div animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.8, repeat: Infinity }}
              style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, var(--cyan), transparent)', boxShadow: '0 0 4px var(--cyan)' }} />
          </motion.div>
        </div>

        <style>{`
          @keyframes shimmer {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          @media (max-width: 768px) {
            .hero-grid { grid-template-columns: 1fr !important; text-align: center; gap: 1.5rem !important; }
            .hero-photo-col { order: -1; display: flex; justify-content: center; width: 100%; }
            .hero-photo-col > div { width: 240px !important; }
            .hero-grid > div:first-child { display: flex; flex-direction: column; align-items: center; }
            .photo-name-tag { display: none !important; }
            .mobile-name-tag { display: block !important; }
          }
          @media (max-width: 480px) {
            .hero-photo-col > div { width: 200px !important; }
          }
        `}</style>
      </section>
    </>
  );
}